/* eslint-disable arrow-body-style */
import AssetManager from 'AssetManager';
import { fromJS, is, Map, merge, mergeWith } from 'immutable';
import { DefaultDataGenerator } from '@pearsontechnology/aero-plugin-schema';
import { isImmutable } from 'pluginServices/helpers/immutableHelpers';
import ActivityService from 'services/ActivityService';
import PathwayService from 'services/PathwayService';
import ScenarioService from 'services/ScenarioService';
import ScopeService from 'services/ScopeService';
import previewService from 'services/PreviewService';
import AnnotationService from 'services/AnnotationService';
import CoursewareTargetService from 'pluginServices/services/CoursewareTargetService';
import * as coursewareTypes from 'pluginServices/constants/coursewareTypes';
import { LEARNER, EDITOR } from 'pluginServices/constants/context';
import InteractiveResolver from 'resolvers/RTMResolvers/InteractiveResolver';
import { createVersionExpression } from 'utils/versionExpressions';
import { utils } from '@pearsontechnology/aero-stage-presentation-framework/dist/rce';
import { Stage } from '@pearsontechnology/aero-stage-presentation-framework/dist/rce/model';
import { composeState } from 'utils/scope/composeScope';
import scopeBindingManager from 'ScopeBindingManager';
import { createComponentReference, generateNewDataIds } from 'containers/Clipboard/helpers';

import PluginService from '../PluginService';
import ComponentService from '../ComponentService';
import { INTERACTIVE } from 'services/ActivityService/elementTypes';
import featureFlagService, { featureKeys } from 'services/FeatureFlagService';
import { deleteUnusedComponents, getComponentUsage } from './pruneUnusedComponents';

import {
  CONFIG_CHANGE,
  CREATED, DELETED,
  SCENARIO_REORDERED,
  DUPLICATED,
  ANNOTATION_CREATED,
  ANNOTATION_UPDATED,
  ANNOTATION_DELETED,
} from './interactiveChangeTypes';
import * as keyPaths from './keyPaths';
import { getCoursewareList, addChildInConfig } from './utils';

export default (() => {
  /** @type {InteractiveResolver} */
  let interactiveResolver;
  let isRegistered = false;

  /** @type Map<string, InteractiveDTO> */
  let _interactiveCache = Map();
  const _subscriptions = {};
  let isSPF2Loaded = false;



  function _createUnsubscribe(interactiveId, listener) {
    return () => {
      const listenerIndex = _subscriptions[interactiveId].indexOf(listener);
      _subscriptions[interactiveId].splice(listenerIndex, 1);
      if (_subscriptions[interactiveId].length === 0) {
        _forgetInteractive(interactiveId);
      }
    };
  }

  function _notifyListeners(interactiveId, context, path) {
    if (_subscriptions[interactiveId]) {
      const listeners = _subscriptions[interactiveId];
      const updatedInteractiveObject = _interactiveCache.get(interactiveId).toJS();
      listeners.forEach((listener) => {
        listener(updatedInteractiveObject, context, path);
      });
    }
  }

  function _forgetInteractive(interactiveId) {
    _interactiveCache = _interactiveCache.remove(interactiveId);
  }

  function updateAssetManager(interactive) {
    const assets = interactive.getIn(keyPaths.ASSETS);
    if (assets) {
      AssetManager.addResolvedToCache(assets.toJS());
    }
  }

  /**
 * Fetches the Plugin Schema, given a pluginId and version
 * @todo Remove this.  Client code should use the PluginService directly
 * @param {string} pluginId
 * @param {string} pluginVersion
 */
  const getPluginSchema = async (pluginId, pluginVersion) => {
    const response = await PluginService.getPlugin(pluginId, pluginVersion);
    return JSON.parse(response.manifest.configurationSchema);
  };

  function _parseAndSaveToCache(jsInteractive) {
    let updatedJSInteractive;

    if (typeof jsInteractive.config === 'string') {
      try {
        const config = JSON.parse(jsInteractive.config);
        if (config.stage && !config.stage.ref) { config.stage = Stage.fromObject(config.stage); }

        updatedJSInteractive = { ...jsInteractive, config };
      } catch (e) {
        throw new Error(`Interactive ${jsInteractive.interactiveId} config parse error: ${jsInteractive.config}`);
      }
    }
    const interactive = fromJS(updatedJSInteractive || jsInteractive);
    updateAssetManager(interactive);
    _interactiveCache = _interactiveCache.set(jsInteractive.interactiveId, interactive);
  }
  /**
   * Adds a new component to the interactive
   * @param {string} interactiveId
   * @param {string} componentId
   */
  async function setComponentInInteractive(interactiveId, componentId) {
    const COMPONENTS_PATH = 'components';
    const existingComponents = await getInInteractive(
      interactiveId,
      [COMPONENTS_PATH]
    ) || [];
    existingComponents.push(componentId);
    _interactiveCache = _interactiveCache.setIn([interactiveId, COMPONENTS_PATH], fromJS([...existingComponents, componentId]));
  }

  /**
 * Creates a new interactive
 * @param {string} pathwayId
 * @param {string} pluginId
 * @param {string} pluginVersion
 * @param {string} title
 * @param {string} interactiveSchema
 */
  const createInteractive = async (pathwayId, pluginId, pluginVersion, title, interactiveSchema, description) => {
    const generator = new DefaultDataGenerator();
    const defaultConfig = generator.generateDefaultData(interactiveSchema);
    const expressedPluginVersion = createVersionExpression(pluginVersion);
    if (title && title.trim() !== '') {
      defaultConfig.title = title;
    } else if (typeof defaultConfig.title === 'undefined') {
      defaultConfig.title = 'Untitled';
    }

    if (description && description.trim() !== '') {
      defaultConfig.description = description;
    }

    defaultConfig.__metadata = { pluginVersion };

    const interactive = await interactiveResolver.authorInteractiveCreate({
      pathwayId,
      pluginId,
      pluginVersion: expressedPluginVersion,
      config: JSON.stringify(defaultConfig),
    });
    const interactiveWithPluginVersion = { ...interactive, plugin: { ...interactive.plugin, latestVersion: pluginVersion } };

    _parseAndSaveToCache(interactiveWithPluginVersion);

    await PathwayService.notifyUpdate(pathwayId);
    return interactive.interactiveId;
  };

  /**
   * Adds a child to an interactive
   * @param {Object} parentInfo
   * @param {string} parentInfo.parentInteractiveId
   * @param {string} parentInfo.parentPluginSchema
   * @param {string} parentInfo.parentInteractivePath
   * @param {Object} newChildItem
   * @param {string} newChildItem.itemId
   */
  const addChildInInteractive = async (parentInfo, newChildItem) => {
    const { parentInteractiveId, parentPluginSchema, parentInteractivePath } = parentInfo;
    const oldConfig = _interactiveCache.getIn([parentInteractiveId, ...keyPaths.config]);
    const newParentConfig = addChildInConfig(
      newChildItem, parentPluginSchema, parentInteractivePath, oldConfig
    );
    await saveInteractiveConfig(parentInteractiveId, newParentConfig);
    return newChildItem.itemId;
  };

  /**
   * Delete an interactive from a pathway.
   * @param {string} interactiveId
   * @param {string} parentPathwayId
   * @param {boolean} deleteInteractiveId
   */
  const deleteInteractive = async (interactiveId, parentPathwayId, deleteInteractiveId = true) => {
    // Deregister the screen and components which are being uplifted
    const interactive = await getInteractive(interactiveId);
    const pathway = await PathwayService.getPathway(parentPathwayId);
    const activity = await ActivityService.getActivity(pathway?.parentActivityId);

    if (deleteInteractiveId) {
      // Get the scope information for the interactive
      const registeredScopes = await ScopeService.getAuthorSourceScopeList(interactive.studentScopeURN,
        interactive.interactiveId, INTERACTIVE);

      // Register interactive and component from ancestor Activity. This does it for all courseware items.
      // TODO - Only deregister the interactive and components already registered on the activity scope
      if (registeredScopes) {
        for (const scope of registeredScopes) {
          await authorStudentScopeDeregister(interactive.interactiveId, scope.elementId,
            activity?.studentScopeURN, scope.elementType);
        }
      }

      await authorStudentScopeDeregisterInteractive(interactiveId);

      await interactiveResolver.authorInteractiveDelete(
        interactiveId,
        parentPathwayId,
      );
    }
    await AnnotationService.invalidateAnnotationsInCache(interactiveId);
    await PathwayService.notifyUpdate(parentPathwayId);
  };

  /**
   * Checks to see if an interactive exists in the cache
   * @param {string} id interactive Id
   */
  const has = (id) => (
    _interactiveCache.has(id));

  /**
   * Checks to see if two interactive paths are the same
   * @param {string} id interactive Id
   */
  const samePath = (pathA, pathB) => (pathA[0] === pathB[0]);

  /**
   * Gets the interactive from either the cache or the RTM.
   * @param {string} interactiveId
   * @param {function(InteractiveDTO)} callback
   * @param {boolean} bypassCache - should be used in exceptional cases only to get
   * fresh interactive data. Typical case is for tagged objectives which are updated by graphql calls
   * but on the same interactive cache is not updated with the linked doc info
   */
  const getInteractive = async (interactiveId, callback, bypassCache = false) => {
    if (!interactiveId) {
      return null;
    }

    if (!has(interactiveId) || bypassCache) {
      const interactive = await interactiveResolver.authorInteractiveGet(
        interactiveId,
      );
      _parseAndSaveToCache(interactive);
    }

    /** @type {InteractiveDTO} */
    const interactive = _interactiveCache.get(interactiveId).toJS();

    scopeBindingManager.saveBoundReferences(interactive.interactiveId, interactive.config);

    if (callback) { callback(interactive); }
    return interactive;
  };

  /**
   * Gets a subsection of an interactive.
   * @param {string} interactiveId
   * @param {[string]} keyPath A immutableJS keypath
   */
  const getInInteractive = async (interactiveId, keyPath = []) => {
    await getInteractive(interactiveId);
    const selectedData = _interactiveCache.getIn([interactiveId, ...keyPath]);

    if (selectedData === null || typeof selectedData === 'undefined') {
      return selectedData;
    }
    return isImmutable(selectedData) ? selectedData.toJS() : selectedData;
  };

  const getInInteractiveConfig = async (interactiveId, keyPath = [], context = EDITOR, interactivePath) => (
    context === EDITOR ? getInInteractive(interactiveId, [...keyPaths.config, ...keyPath]) : getInInteractivePreview(interactiveId, keyPath, interactivePath)
  );

  const getInInteractivePreview = (interactiveId, keyPath, interactivePath) => (
    samePath(keyPath, interactivePath) ? previewService.getInPreviewScope([interactiveId, ...keyPath]) : null
  );

  /**
   * Subscribes to an interactive. Call back is triggered on subscription,
   * and every subsequent change to the interactive.
   * @param {string} interactiveId
   * @param {function(InteractiveDTO)} listener
   * @returns {function(void)} Unsubscribe function
   */
  function subscribeToInteractive(interactiveId, listener) {
    _subscriptions[interactiveId] = _subscriptions[interactiveId] || [];
    _subscriptions[interactiveId].push(listener);
    if (has(interactiveId)) {
      listener(_interactiveCache.get(interactiveId).toJS());
    } else {
      getInteractive(interactiveId, listener);
    }

    return _createUnsubscribe(interactiveId, listener);
  }

  /**
   * Subscribes to a keypath inside the interactive, rather than the entire interactive
   * @param {string} interactiveId
   * @param {string} keyPath
   * @param {function(*)} listener
   * @returns {function(void)} Unsubscribe function
   */
  function subscribeToInteractiveConfig(interactiveId, keyPath = [], listener) {
    // TODO: subscribe to only a part of the interactive (It would mean knowing when only
    // that part changed instead of knowing that the whole interactive changed)
    const listenerToRootInteractive = async (interactiveObj, context, interactivePath) => {
      const interactivePart = await getInInteractiveConfig(interactiveId, keyPath, context, interactivePath);
      if (interactivePart) {
        listener(interactivePart);
      }
    };
    return subscribeToInteractive(interactiveId, listenerToRootInteractive);
  }

  function receiveInteractiveChange(broadcast) {
    const {
      elementId: interactiveId, action,
      config, parentPathwayId, lifecycle,
      annotationId, elementId, elementType,
    } = broadcast.response;
    if (action === CREATED || action === DELETED || action === DUPLICATED) {
      PathwayService.notifyUpdate(parentPathwayId);
    } else if (action === SCENARIO_REORDERED) {
      ScenarioService.notifyUpdate(interactiveId, lifecycle);
    } else if (action === CONFIG_CHANGE && _interactiveCache.has(interactiveId)) {
      const interactive = _interactiveCache.get(interactiveId).toJS();
      interactive.config = config;
      _parseAndSaveToCache(interactive);
      _notifyListeners(interactiveId);
    } else if (action === ANNOTATION_CREATED || action === ANNOTATION_UPDATED) {
      AnnotationService.notifyUpdate(annotationId, elementId, elementType);
    } else if (action === ANNOTATION_DELETED) {
      AnnotationService.notifyDelete(annotationId);
    }
  }

  function generateNewDataIdsForInteractiveConfig(interactiveConfig) {
    interactiveConfig.stage?.sections?.forEach((section) => {
      section?.rows?.forEach((row) => {
        row?.columns?.forEach((column) => {
          column?.stageElements?.forEach((element, index) => {
            column.stageElements[index] = generateNewDataIds(element);
          });
        });
      });
    });
    return JSON.stringify(interactiveConfig);
  }

  function generateNewRefIdsForComponent(interactiveConfig) {
    if (interactiveConfig.components) {
      interactiveConfig.components?.forEach((component) => {
        const newLocalRef = createComponentReference(component.itemId).localRef;
        const didReplaceRef = replaceRefInStageElements(
          component.localRef,
          newLocalRef,
          interactiveConfig
        );

        if (didReplaceRef) {
          component.localRef = newLocalRef;
        }
      });
    }

    return JSON.stringify(interactiveConfig);
  }

  function replaceRefInStageElements(oldRef, newRef, interactiveConfig) {
    const stage = interactiveConfig.stage;
    let didReplaceRef = false;
    stage?.sections?.forEach((section) => {
      section.rows?.forEach((row) => {
        row.columns?.forEach((column) => {
          const stageElement = column.stageElements?.find(
            (element) => element.localRef === oldRef
          );
          if (stageElement) {
            stageElement.localRef = newRef;
            stageElement.ref = newRef;
            didReplaceRef = true;
            return;
          }
        });
      });
    });

    return didReplaceRef;
  }

  /**
   * Duplicates an interactive into a pathway.
   * @param {string} interactiveId
   * @param {string} pathwayId
   * @param {number} index
   */
  const duplicateInteractiveInPathway = async (interactiveId, pathwayId, index, customErrorMessage) => {
    const isGenerateUniqueLocalRefEnabled = featureFlagService.is(featureKeys.WORKSPACE_COPY_GENERATE_UNIQUE_LOCAL_REF_IDS);
    const payload = { interactiveId, pathwayId, index, customErrorMessage };
    const newInteractive = await interactiveResolver.authorInteractiveDuplicate(payload);
    let newInteractiveConfig = generateNewDataIdsForInteractiveConfig(JSON.parse(newInteractive.config));

    if (isGenerateUniqueLocalRefEnabled) {
      newInteractiveConfig = generateNewRefIdsForComponent(
        JSON.parse(newInteractiveConfig)
      );
    }

    newInteractive.config = newInteractiveConfig;
    await interactiveResolver.authorInteractiveConfigReplace(newInteractive.interactiveId, newInteractiveConfig);
    scopeBindingManager.saveBoundReferences(newInteractive.interactiveId, newInteractiveConfig);

    const target = await CoursewareTargetService.build().createTarget(newInteractive.interactiveId);
    await AnnotationService.refetchAnnotations(target);
    _parseAndSaveToCache(newInteractive);

    await PathwayService.notifyUpdate(pathwayId);
    return newInteractive.interactiveId;
  };

  /**
   * Move components to a different interactive
   * @param {string} interactiveId
   * @param {[string]} componentIds
   */
  const moveInteractiveComponents = async (componentIds, interactiveId) => {
    await interactiveResolver.authorInteractiveMove({
      componentIds,
      interactiveId,
      elementType: coursewareTypes.INTERACTIVE,
    });
  };

  /**
   * Saves the interactive's config
   * @param {string} interactiveId
   * @param {*} newConfig
   */
  async function saveInteractiveConfig(interactiveId, newConfig, isConfigVerificationNeeded = false) {
    let currentConfig = newConfig;
    const prevConfig = await getInInteractiveConfig(interactiveId);
    if (newConfig instanceof Map) {
      currentConfig = newConfig.toJS();
    }

    if (JSON.stringify(prevConfig) === JSON.stringify(currentConfig)) return;

    // Verify config for componenets like mmi or inline-dropdown that dont have any ref in stage 
    let unusedComponents = [];
    if (currentConfig?.components?.length > 0 && isConfigVerificationNeeded) {
      const { activeComponents, unusedComponents: unused } = await getComponentUsage(currentConfig, interactiveId);
      currentConfig.components = activeComponents;
      unusedComponents = unused;
    }

    let updatedConfig = currentConfig;
    _dangerouslySetConfigInCache(interactiveId, updatedConfig);
    updatedConfig = { ...updatedConfig, __metadata: { pluginVersion: _interactiveCache.get(interactiveId).toJS().plugin.latestVersion } };
    await interactiveResolver.authorInteractiveConfigReplace(interactiveId, JSON.stringify(updatedConfig));
    scopeBindingManager.saveBoundReferences(interactiveId, updatedConfig);
    unusedComponents.length > 0 && await deleteUnusedComponents(interactiveId, unusedComponents);
    return interactiveId;
  }

  /**
   * Updates a section of the interactive's configuration, and then
   * saves it.
   * @param {string} interactiveId
   * @param {[string]} keyPath
   * @param {*} newConfigPart
   * @param {string?} stageRef
   */
  async function saveInInteractiveConfig(interactiveId, keyPath, newConfigPart = null, stageRef) {
    if (!_interactiveCache.has(interactiveId)) {
      return null;
    }
    const oldConfig = _interactiveCache.getIn([interactiveId, ...keyPaths.config]) || fromJS({});
    // for saving nested stage
    if (stageRef) {
      let newConfig = {};
      const oldConfigInJS = oldConfig.toJS();
      const objKeysToDeepMerge = ['pluginConfigs'];

      // 1. find the nested stage and its path
      const { element: stage, keyPathToElement: keyPathToStage } = utils.getStageElementByRefRecursively(oldConfigInJS, stageRef) || {};
      // 2. replace the config within targetStage
      const oldConfigPart = oldConfig.hasIn(keyPathToStage.concat(keyPath)) ? oldConfig.getIn(keyPathToStage.concat(keyPath)) : new Map();
      const updatedStage = fromJS(stage).setIn(keyPath, mergeWith((prev, next, key) => {
        if (objKeysToDeepMerge.includes(key)) {
          return merge(prev, next);
        }
        return next;
      }, oldConfigPart, newConfigPart));
      // 3. replace the targetStage inside the full stage model
      newConfig = oldConfig.setIn(keyPathToStage, updatedStage);

      return saveInteractiveConfig(interactiveId, newConfig);
    }

    if (keyPath.length) {
      const newConfig = oldConfig.setIn(keyPath, newConfigPart);
      return saveInteractiveConfig(interactiveId, newConfig, true);
    }

    return saveInteractiveConfig(interactiveId, merge(oldConfig, newConfigPart));
  }

  function saveInInteractivePreviewScope(interactiveId, keyPath, newConfigPart) {
    let oldConfig = {}; let newScope = {};
    const interactiveScope = previewService.getInPreviewScope([interactiveId]);
    if (!_interactiveCache.has(interactiveId) || !interactiveScope) {
      return null;
    }
    // if interactivePreviewScope is there - update it
    if (interactiveScope) {
      // set in scope and return the updated preview scope
      newScope = previewService.setPreviewScope(keyPath, newConfigPart);
    } else {
      oldConfig = _interactiveCache.getIn([interactiveId, ...keyPaths.config]) || fromJS({});
      newScope = oldConfig.setIn(keyPath, newConfigPart);
    }
    setInPreviewScope(interactiveId, newScope, keyPath);
    return interactiveId;
  }

  /**
  * Setting in scope for preview functionality
  * the InteractiveConfig is not updated
  * @param {*} interactiveId
  * @param {*} newScope
  */
  const setInPreviewScope = async (interactiveId, newScope, path = []) => {
    const config = _interactiveCache.getIn([interactiveId, ...keyPaths.config]);
    const { pluginId, version } = _interactiveCache.getIn([interactiveId, 'plugin']).toJS();
    const schema = await getPluginSchema(pluginId, version);
    const previewState = composeState(schema, config, newScope);
    previewService.setPreviewScope([interactiveId], previewState);
    if (!is(config, fromJS(previewState))) {
      _notifyListeners(interactiveId, LEARNER, path);
    }
  };

  // This is how we can get an invalid cache (not waiting for backend response)
  function _dangerouslySetConfigInCache(interactiveId, newConfig) {
    const jsConfig = isImmutable(newConfig) ? newConfig.toJS() : newConfig;
    const immutableConfig = fromJS(jsConfig);

    // save and notify only if the interactive config is different to what we already have
    const oldConfig = _interactiveCache.getIn([interactiveId, ...keyPaths.config]);
    if (!is(oldConfig, immutableConfig)) {
      _interactiveCache = _interactiveCache.setIn([interactiveId, ...keyPaths.config], immutableConfig);
      _notifyListeners(interactiveId);
    }
  }

  const _getConfigDataAndSummary = async (courseware) => {
    let configurationData;
    let pluginSummary;
    const { id: coursewareID, type: coursewareType } = courseware;

    if (coursewareType === coursewareTypes.INTERACTIVE) {
      configurationData = await getInInteractiveConfig(coursewareID);
      pluginSummary = await getInInteractive(coursewareID, keyPaths.pluginRef);
    } else if (coursewareType === coursewareTypes.COMPONENT) {
      configurationData = await ComponentService.getInComponentConfig(coursewareID);
      pluginSummary = await ComponentService.getInComponent(coursewareID, keyPaths.pluginRef);
    }
    return { configurationData, pluginSummary };
  };

  const getPluginSummary = async (courseware) => {
    let pluginSummary;
    const { id: coursewareID, type: coursewareType } = courseware;

    if (coursewareType === coursewareTypes.INTERACTIVE) {
      pluginSummary = await getInInteractive(coursewareID, keyPaths.pluginRef);
    } else if (coursewareType === coursewareTypes.COMPONENT) {
      pluginSummary = await ComponentService.getInComponent(coursewareID, keyPaths.pluginRef);
    }
    return { pluginSummary };
  };

  const getSchemasInInteractive = async (id, category) => {
    const coursewareList = await getCoursewareList({ itemId: id });
    const studentScopeURN = await getInInteractive(id, ['studentScopeURN']);

    return Promise.all(coursewareList.map(async (courseware) => {
      const { id: coursewareID } = courseware;
      const { configurationData, pluginSummary } = await _getConfigDataAndSummary(courseware);
      const { pluginId, version, name: pluginName } = pluginSummary;
      const plugin = await PluginService.getPlugin(pluginId, version);
      const configurationSchema = JSON.parse(plugin.manifest.configurationSchema || '{}');

      return {
        key: coursewareID,
        // TODO: Check whether coursewareID === interactiveID (i.e. not a component),
        // and don't add `.scope` in that case, since the interactive is not part of the scope.
        // But we could add the interactive to the scope so the backend can evaluate it.
        scope: {
          sourceId: coursewareID,
          studentScopeURN,
        },
        label: (configurationData && configurationData.title) || pluginName || coursewareID,
        category,
        schema: { ...configurationSchema },
      };
    }));
  };

  /**
   * Registers a component to a student scope.
   * The only difference to authorStudentScopeRegisterInteractive is that this
   * registers a component, rather than the interactive.
   * @param {string} interactiveId
   * @param {string} componentId
   */
  const authorStudentScopeRegister = async (interactiveId, componentId, studentScopeURN = undefined, elementType = undefined, bypassCache = false) => {
    if (!has(interactiveId) && !bypassCache) {
      throw new Error('Interactive not available');
    }

    const interactive = _interactiveCache.get(interactiveId)?.toJS();

    await interactiveResolver.authorStudentScopeRegister({
      studentScopeURN: studentScopeURN || interactive.studentScopeURN,
      elementId: componentId,
      elementType: elementType || coursewareTypes.COMPONENT,
    });
  };

  /**
   * Deregisters a component from a student scope
   * @param {string} interactiveId
   * @param {string} componentId
   */
  const authorStudentScopeDeregister = async (interactiveId, componentId, studentScopeURN = undefined, elementType = undefined) => {
    if (!has(interactiveId)) {
      throw new Error('Interactive not available');
    }

    const interactive = _interactiveCache.get(interactiveId).toJS();

    await interactiveResolver.authorStudentScopeDeregister({
      studentScopeURN: studentScopeURN || interactive.studentScopeURN,
      elementId: componentId,
      elementType: elementType || coursewareTypes.COMPONENT,
    });
  };

  /**
   * Registers an interactive to a student scope.
   * @param {string} interactiveId
   */
  const authorStudentScopeRegisterInteractive = async (interactiveId) => {
    if (!has(interactiveId)) {
      throw new Error('Interactive not available');
    }

    const interactive = _interactiveCache.get(interactiveId).toJS();

    await interactiveResolver.authorStudentScopeRegister({
      studentScopeURN: interactive.studentScopeURN,
      elementId: interactiveId,
      elementType: coursewareTypes.INTERACTIVE,
    });
  };

  /**
   * Deregisters an interactive from a student scope
   * @param {string} interactiveId
   * @param {string} componentId
   */
  const authorStudentScopeDeregisterInteractive = async (interactiveId) => {
    if (!has(interactiveId)) {
      await getInteractive(interactiveId);
    }

    const interactive = _interactiveCache.get(interactiveId).toJS();

    await interactiveResolver.authorStudentScopeDeregister({
      studentScopeURN: interactive.studentScopeURN,
      elementId: interactiveId,
      elementType: coursewareTypes.INTERACTIVE,
    });
  };

  const isCurrentScreenWithSPF2 = () => isSPF2Loaded;

  const setIsCurrentScreenWithSpf2 = (value) => {
    isSPF2Loaded = value;
  };

  /**
   * Move interactive to a different pathway
   * @param {string} interactiveId
   * @param {[string]} pathwayId
   * @param {int} index
   */
  const moveInteractiveToPathway = async (interactiveId, pathwayId, index = '', isIndexAllowed = false) => {
    await interactiveResolver.authorInteractiveMoveToPathway({
      interactiveId,
      pathwayId,
      index,
      isIndexAllowed,
    });
  };

  /**
    * Add conditional to an element for MD.
    * @param {Object} payload - The payload for adding conditional.
    * @param {string} payload.courseId - The course ID (timeuuid).
    * @param {string} payload.elementId - The element ID (timeuuid).
    * @param {string} payload.elementType - The type of element: ACTIVITY, INTERACTIVE, or COMPONENT.
    * @param {string} [payload.referenceId] - Optional reference ID for items within the element.
    * @param {string} payload.conditionalType - The conditional type: OUTPUT_TYPE or MARKET_DERIVATIVE.
    */
  const addElementConditional = async (payload) => {
    await interactiveResolver.coursewareElementConditionalAdd(payload);
  };

  /**
   * Remove conditional from an element for MD.
   * @param {Object} payload - The payload for removing conditional.
   * @param {string} payload.courseId - The course ID (timeuuid).
   * @param {string} payload.elementId - The element ID (timeuuid).
   * @param {string} [payload.referenceId] - Optional reference ID for items within the element.
   * @param {string} payload.conditionalType - The conditional type: OUTPUT_TYPE or MARKET_DERIVATIVE.
   */
  const removeElementConditional = async (payload) => {
    await interactiveResolver.coursewareElementConditionalRemove(payload);
  };

  return {
    isRegistered: () => isRegistered,

    register: (connectionProvider) => {
      isRegistered = true;
      interactiveResolver = new InteractiveResolver(connectionProvider);
    },

    createInteractive,
    deleteInteractive,
    addChildInInteractive,

    has,
    getInteractive,
    getInInteractive,
    getInInteractiveConfig,
    getInInteractivePreview,

    subscribeToInteractive,
    subscribeToInteractiveConfig,
    receiveInteractiveChange,

    duplicateInteractiveInPathway,

    moveInteractiveComponents,

    saveInteractiveConfig,
    saveInInteractiveConfig,
    setInPreviewScope,
    saveInInteractivePreviewScope,
    setComponentInInteractive,

    getSchemasInInteractive,

    authorStudentScopeRegister,
    authorStudentScopeRegisterInteractive,
    authorStudentScopeDeregister,

    getPluginSummary,
    getPluginSchema,
    isCurrentScreenWithSPF2,
    setIsCurrentScreenWithSpf2,
    moveInteractiveToPathway,
    addElementConditional,
    removeElementConditional,
  };
})();
